import {
  ElementType,
  Planet,
  PlanetClassType,
  PlanetSkill,
  PlanetSkillSummary,
  PlanetSkillType
} from '@apeiron/planet'
import { createStrictContext, LOCAL_LIST_SIZE } from '@apeiron/temp'
import { SkillProvider } from '@src/contexts/skill/SkillContext'
import useFetchPlanetSkillJson from '@src/hooks/json/useFetchPlanetSkillJson'
import * as R from 'ramda'
import { FC, ReactNode, useCallback, useMemo } from 'react'

const [ContextProvider, usePlanetSkillsContext] =
  createStrictContext<ContextProps>('PlanetSkills')

export { usePlanetSkillsContext }

export const PlanetSkillsProvider: FC<Props> = (props: Props) => {
  const { children, defaultDetail = false } = props

  const { data } = useFetchPlanetSkillJson()

  const skillPool = useMemo(
    () =>
      data || {
        autoAttack: [],
        active: [],
        passive: [],
        bindPassive: [],
        bindEnergized: [],
        allPassive: [],
        all: []
      },
    [data]
  )

  const sortSkills = useCallback((skills: PlanetSkill[]): PlanetSkill[] => {
    const sort = R.sortWith<PlanetSkill>([
      R.descend(R.path<any>(['attributes', 'planetClass'])),
      R.ascend(R.path<any>(['attributes', 'element'])),
      R.ascend(R.prop('geneId'))
    ])

    return sort(skills as PlanetSkill[])
  }, [])

  const pageSkills = useCallback((skills: PlanetSkill[]) => {
    return R.take(LOCAL_LIST_SIZE, skills)
  }, [])

  const pageAndSortSkills = useCallback(
    (skills: PlanetSkill[]) => {
      const paged = pageSkills(skills)

      const sorted = sortSkills(paged)

      return sorted
    },
    [pageSkills, sortSkills]
  )

  const autoAttacks = skillPool.autoAttack

  const activeSkills = useMemo(
    () => pageAndSortSkills(skillPool.active),
    [pageAndSortSkills, skillPool.active]
  )

  const allSkills = skillPool.all

  const bindedEnergizedSkills = useMemo(
    () => skillPool.bindEnergized,
    [skillPool]
  )

  const bindedPassiveSkills = skillPool.bindPassive

  const passiveSkills = useMemo(
    () => pageAndSortSkills(skillPool.passive),
    [skillPool.passive, pageAndSortSkills]
  )

  const skillSummary = useMemo((): PlanetSkillSummary => {
    const extractNumberOfCardSkill = R.pipe<any, any, any, any>(
      R.groupBy((skill: PlanetSkill) => String(skill.attributes.planetClass)),
      R.map((skills: PlanetSkill[]) =>
        R.groupBy((skill: PlanetSkill) => skill.attributes.element, skills)
      ),
      R.map(R.map((skills: PlanetSkill[]) => R.length(skills)))
    )

    const extractNumberOfPerkSkill = R.pipe<any, any, any>(
      R.groupBy((skill: PlanetSkill) => skill.attributes.element),
      R.map((skill: PlanetSkill[]) => R.length(skill))
    )

    return {
      numberOfCardSkill: extractNumberOfCardSkill(activeSkills),
      numberOfPerkSkill: extractNumberOfPerkSkill(passiveSkills)
    }
  }, [activeSkills, passiveSkills])

  const searchSkills = useCallback(
    (params: SearchSkillParams): PlanetSkill[] => {
      const { type } = params

      let skills: PlanetSkill[] = []

      switch (type) {
        case PlanetSkillType.AutoAttack:
          {
            const { planet } = params as SearchAutoAttackParams

            if (planet) {
              skills = R.filter(
                (skill: PlanetSkill) =>
                  skill.attributes.planetClass === planet.planetClass &&
                  skill.attributes.weapon === planet.weapon,
                autoAttacks
              )
            }
          }

          break

        case PlanetSkillType.Active:
          {
            const { cSkillID, elements, planetClasses, planet } =
              params as SearchActiveSkillParams

            if (elements && planetClasses) {
              skills =
                R.isEmpty(elements) && R.isEmpty(planetClasses)
                  ? activeSkills
                  : R.filter((skill: PlanetSkill) => {
                      const passedElement =
                        R.isEmpty(elements) ||
                        R.includes(skill.attributes.element, elements)

                      const passedPlanetClass =
                        R.isEmpty(planetClasses) ||
                        R.includes(skill.attributes.planetClass, planetClasses)

                      return passedElement && passedPlanetClass
                    }, activeSkills)
            } else if (cSkillID) {
              skills = R.filter(
                (skill: PlanetSkill) => R.includes(skill.uniqueKey, cSkillID),
                activeSkills
              )
            } else if (planet) {
              skills = R.filter(
                (skill: PlanetSkill) =>
                  skill.attributes.planetClass === planet.planetClass &&
                  (skill.geneId === planet.cSkill1 ||
                    skill.geneId === planet.cSkill2 ||
                    skill.geneId === planet.cSkill3),
                activeSkills
              )
            }
          }

          break

        case PlanetSkillType.Passive:
          {
            const { elements, planet, pSkillID } =
              params as SearchPassiveSkillParams

            if (elements) {
              skills = R.isEmpty(elements)
                ? passiveSkills
                : R.filter(
                    (skill: PlanetSkill) =>
                      R.includes(skill.attributes.element, elements),
                    passiveSkills
                  )
            } else if (pSkillID) {
              skills = R.filter(
                (skill: PlanetSkill) => R.includes(skill.geneId, pSkillID),
                passiveSkills
              )
            } else if (planet) {
              skills = R.filter(
                (skill: PlanetSkill) =>
                  skill.geneId === planet.pSkill1 ||
                  skill.geneId === planet.pSkill2,
                passiveSkills
              )
            }
          }

          break

        case PlanetSkillType.PlanetPassive:
          {
            const { planet } = params as SearchBindedPassiveSkillParams

            if (planet) {
              skills = R.filter(
                (skill: PlanetSkill) =>
                  skill.attributes.planetType === planet.planetType,
                bindedPassiveSkills
              )
            }
          }

          break

        case PlanetSkillType.PlanetEnergize:
          {
            const { planet } = params as SearchBindedEnergizeSkillParams

            if (planet) {
              skills = R.filter(
                (skill: PlanetSkill) =>
                  skill.attributes.planetType === planet.planetType &&
                  skill.attributes.planetClass === planet.planetClass,
                bindedEnergizedSkills
              )
            }
          }

          break
      }

      return pageAndSortSkills(skills)
    },
    [
      activeSkills,
      autoAttacks,
      bindedEnergizedSkills,
      bindedPassiveSkills,
      passiveSkills,
      pageAndSortSkills
    ]
  )

  const getElementReferenceNo = useCallback(
    (skill: PlanetSkill) => {
      switch (skill.type) {
        case PlanetSkillType.Active: {
          const numberOfSkill = R.pathOr(
            1,
            [
              'numberOfCardSkill',
              skill.attributes.planetClass,
              skill.attributes.element
            ],
            skillSummary
          )

          return `C${(skill.geneId % numberOfSkill) + 1}`
        }

        case PlanetSkillType.Passive: {
          const numberOfSkill = R.pathOr(
            1,
            ['numberOfPerkSkill', skill.attributes.element],
            skillSummary
          )

          return `P${(skill.geneId % numberOfSkill) + 1}`
        }

        default:
          return ''
      }
    },
    [skillSummary]
  )

  const getSkill = useCallback(
    (params: SearchSkillParams): PlanetSkill | null => {
      const skills = searchSkills(params)

      return R.propOr(null, '0', skills) as PlanetSkill | null
    },
    [searchSkills]
  )

  const contextValue = useMemo<ContextProps>(() => {
    return {
      activeSkills,
      allSkills,
      bindedEnergizedSkills,
      bindedPassiveSkills,
      passiveSkills,
      skillSummary,
      getElementReferenceNo,
      getSkill,
      sortSkills,
      searchSkills
    }
  }, [
    activeSkills,
    allSkills,
    bindedEnergizedSkills,
    bindedPassiveSkills,
    passiveSkills,
    skillSummary,
    getElementReferenceNo,
    getSkill,
    sortSkills,
    searchSkills
  ])

  return (
    <SkillProvider defaultDetail={defaultDetail}>
      <ContextProvider value={contextValue}>{children}</ContextProvider>
    </SkillProvider>
  )
}

type SearchAutoAttackParams = {
  planet?: Planet
  type: PlanetSkillType
}

type SearchActiveSkillParams = {
  cSkillID?: string[]
  elements?: ElementType[]
  planetClasses?: PlanetClassType[]
  planet?: Planet
  type: PlanetSkillType
}

type SearchPassiveSkillParams = {
  elements?: ElementType[]
  planet?: Planet
  pSkillID?: number[]
  type: PlanetSkillType
}

type SearchBindedPassiveSkillParams = {
  planet?: Planet
  type: PlanetSkillType
}

type SearchBindedEnergizeSkillParams = {
  planet?: Planet
  type: PlanetSkillType
}

type SearchSkillParams =
  | SearchActiveSkillParams
  | SearchPassiveSkillParams
  | SearchBindedPassiveSkillParams
  | SearchBindedEnergizeSkillParams

export type ContextProps = {
  activeSkills: PlanetSkill[]
  allSkills: PlanetSkill[]
  bindedEnergizedSkills: PlanetSkill[]
  bindedPassiveSkills: PlanetSkill[]
  passiveSkills: PlanetSkill[]
  skillSummary: PlanetSkillSummary
  getElementReferenceNo: (skill: PlanetSkill) => string
  getSkill: (params: SearchSkillParams) => PlanetSkill | null
  sortSkills: (skills: PlanetSkill[]) => PlanetSkill[]
  searchSkills: (params: SearchSkillParams) => PlanetSkill[]
}

export type Props = {
  children?: ReactNode
  defaultDetail?: boolean
}
